import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Container, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,  } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { toast } from 'react-toastify';
import ClassificationBannerHeader from '../../common/banner/classifactionBannerHeader.js';
import PageHeader from '../../common/banner/pageHeader.js';
import { sortAdminItems } from '../../common/utils/tableUtil.js';
import SearchBar from '../../common/search/SearchBar.js';
import AdminAlertModal from './modals/adminAlertModal.js';
import AdminAlertDeleteModal from './modals/adminAlertDeleteModal.js';
import { getSeverityColor } from '../../common/utils/getSeverityColor.js';

const AdminAlertsTable = ({ items, onToggle, sortConfig, onSortChange, fetchAlerts, setAlerts }) => {
  
  const handleUpdate = (alert) =>  {    
    toast.success(`Alert ${alert.id} Updated`, { autoClose: 3000 });
    fetchAlerts();
  }

  const handleDelete = (alert) =>  {
    setAlerts(items.filter(items => items.id !== alert.id));
    toast.success(`Alert ${alert.id} Deleted`, { autoClose: 3000 });
  }
  
  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  }

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUp fontSize="small" />
    ) : (
      <ArrowDropDown fontSize="small" />
    );
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">    
          <TableHead>
            <TableRow sx={{ backgroundColor: '#0568AE' }}>
              {['Reporter', 'Events', 'Severity', 'First Seen', 'Last Seen', 'Sparkline', 'Source', 'ID', 'Alert Message', 'Phen.AI Ignore', 'Reference'].map((field) => (
                <TableCell
                  key={field}
                  onClick={() => handleSort(field)}
                  sx={{ color: '#EEEEEE', fontWeight: 'bold', cursor: 'pointer', attackSelect: 'none' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                    <SortIcon field={field} />
                  </Stack>
                </TableCell>                
              ))}
              <TableCell sx={{ color: '#EEEEEE', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={item.id} sx={{ height: 56, backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#000000' }}>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.sensor}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.events}</TableCell>
                <TableCell>
                  <Chip 
                      label={item.severity} 
                      size="small"
                      sx={{ 
                        backgroundColor: getSeverityColor(item.severity),
                        color: 'white',
                        fontWeight: 'bold',
                        width: '70px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                  />
                </TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.first_seen}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.last_seen}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.sparkline}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.source}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.id}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.message}</TableCell>
                <TableCell>
                    <Switch 
                        checked={!!item.phen_ignore} 
                        onChange={(e) => onToggle(item, e.target.checked)} 
                    />
                </TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.reference}</TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1}>
                        <AdminAlertModal alert={ item } onTriggerUpdate={handleUpdate} />
                        <AdminAlertDeleteModal alert={ item } onTriggerDelete={handleDelete} /> 
                    </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default function AdminAlerts() {
    const [alerts, setAlerts] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fetchAlerts = async () => {
        try {
                
            const response = await fetch(`${process.env.REACT_APP_API}/alerts/`);
            if (!response.ok) {
                toast.error(`HTTP error! status: ${response.status}`);
            } else {
                const json = await response.json();
                setAlerts(json.data);
            }
        } catch (error) {
            toast.error(error);
        } 
    };

    const handleCreate = (alert) =>  {
      toast.success(`Alert ${alert.id} Created`, { autoClose: 3000 });
      fetchAlerts();
    }

    const handleToggle = async (item, value) => {
        
        const updatedItem = { ...item, phen_ignore: value };
        try {
          const res = await fetch(`${process.env.REACT_APP_API}/alerts/${item.id}/ignore`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updatedItem),
          })
          if (res.ok) {
            setAlerts((prev) => prev.map((p) => (p.id === item.id ? updatedItem : p)));
            if (updatedItem.phen_ignore) {
              toast.success(`${updatedItem.sensor} Visible`);
            } else {
              toast.success(`${updatedItem.sensor} Ignored`);
            }

            fetchAlerts();
          } else {
            toast.error(`Error: ${res.status}`);
          }
        } catch (e) {
          console.error(e);
        }
    }

    const handleSortChange = (newConfig) => {
        setSortConfig(newConfig);
    }

    const handleDates = async () =>  {
      try {
          const res = await fetch(`${process.env.REACT_APP_API}/alerts/updateDates`, {
            method: 'POST'
          })
          if (res.ok) {
            toast.success(`Alert Dates Have Been Updated Succesfully`);
            fetchAlerts();
          } else {
            toast.error(`Error: ${res.status}`);
          }
        } catch (e) {
          console.error(e);
        }
    }

    const filtered = alerts?.filter((item) => {
        const s = searchQuery.toLowerCase();
        return ['alert', 'severity', 'source', 'message'].some((field) => item[field]?.toLowerCase().includes(s));
    });

  const sorted = sortAdminItems(filtered, sortConfig);
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
      fetchAlerts();
  }, []);

  return (
    <Container sx={{ minWidth:1800, mt: 10, mb: 10 }}>
        <Box sx={{pt: 2}}>
            <ClassificationBannerHeader />
            <PageHeader title="Admin Alerts" />
            <Stack direction="row" justifyContent="flex-end" mt={2} mb={1} sx={{ width: '100%', mx: 'auto' }}>                    
                  <SearchBar
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search Alerts..."
                      label="Search Alerts..."
                      width={200}
                  />                
            </Stack>
          
            <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: '#1A1A1A'  }}>
                <AdminAlertsTable
                    items={paginated}
                    onToggle={handleToggle}
                    sortConfig={sortConfig}
                    onSortChange={handleSortChange}
                    fetchAlerts={fetchAlerts}
                    setAlerts={setAlerts}
                />
                <TablePagination
                    component="div"
                    count={sorted.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
                />
            </TableContainer>
            <AdminAlertModal alert='' onTriggerCreate={handleCreate} />
            <Button variant="contained" color="primary" sx={{ mt: 2, ml: 2 }} onClick={handleDates} >
                Update Dates
              </Button>
        </Box>
    </Container>
  )
}
